<template>
  <div class="d-flex flex-column pa-2" :style="{ gap: '10px' }">
    <v-toolbar flat>
      <v-toolbar-action>
        <div class="d-flex" :style="{ gap: '10px' }">
          <v-btn text @click="$router.push('/consumers')">
            <v-icon>mdi-account</v-icon>
            Consumers
          </v-btn>
          <v-btn color="blue" @click="$router.push('/consumer-ticket-requests')">
            <v-icon>mdi-ticket</v-icon>
            Consumer Ticket Requests
          </v-btn>
        </div>
      </v-toolbar-action>
    </v-toolbar>
    <div class="d-flex flex-column" :style="{ gap: '10px' }">
      <!-- Filters & Actions -->
      <div class="d-flex flex-column flex-sm-row mb-3 align-center" :style="{ gap: '10px' }">
        <v-text-field v-model="tableFilters.search" outlined label="Search Consumer" dense clearable hide-details
                      @keyup.enter="applyFilters" @change="applyFilters" />
        <v-select :value="tableFilters.status" outlined :items="statusOptions"
                  label="Filter by Status" dense clearable multiple chips small-chips deletable-chips hide-details
                  @change="v => tableFilters = { ...tableFilters, status: v }" />
        <v-btn color="primary" @click="fetchTickets">
          Refresh
        </v-btn>
      </div>

      <!-- Table -->
      <v-data-table :loading="table.loading" :headers="table.headers" :items="table.items">
        <template #item.status="{ value }">
          <v-chip :color="({ pending: 'orange', reviewing: 'blue', closed: 'green' })[value] || 'grey'" dark small>
            {{ value }}
          </v-chip>
        </template>
        <template #item.createdAt="{ value }">
          {{ value ? dateFormatWithTime(new Date(value), 'en-US') : null }}
        </template>
        <template #item.reviewedAt="{ value }">
          {{ value ? dateFormatWithTime(new Date(value), 'en-US') : null }}
        </template>
        <template #item.closedAt="{ value }">
          {{ value ? dateFormatWithTime(new Date(value), 'en-US') : null }}
        </template>
        <template #item.message="{ value }">
          <td>{{ value }}</td>
        </template>

        <template #item._config="{ item }">
          <div class="flex-row d-flex" :style="{ gap: '4px' }">
            <v-btn v-if="item.status !== 'closed'" color="blue" small dark @click="reviewTicket(item)">
              <v-icon left>
                mdi-eye
              </v-icon> Review
            </v-btn>

            <v-btn v-if="item.status !== 'closed'" color="red" small dark @click="closeTicket(item)">
              <v-icon left>
                mdi-close
              </v-icon> Close
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { authAxios } from '@/extra-functions/axios.extra'
import { ErrorPopup, LoadingPopup, useWatchEffect, dateFormatWithTime, SuccessPopup, Swal } from '@/lib/utils'

const [watchEffect, clearEffect] = useWatchEffect()

export default {
  name: 'ConsumerTicketRequestsIndex',
  data() {
    return {
      table: {
        headers: [
          { text: 'Created At', value: 'createdAt' },
          { text: 'Consumer User', value: 'consumerName' },
          { text: 'Message', value: 'message' },
          { text: 'Reply', value: 'reply' },
          { text: 'Status', value: 'status' },
          { text: 'Reviewed At', value: 'reviewedAt' },
          { text: 'Closed At', value: 'closedAt' },
          { text: 'Actions', value: '_config', sortable: false },
        ],
        loading: false,
        items: [],
      },
      tableFilters: {
        search: null,
        status: ['pending', 'reviewing'], // backend default
      },
      statusOptions: ['pending', 'reviewing', 'closed'],
      websocketListeners: [],
    }
  },
  watch: {
    tableFilters() {
      this.fetchTickets()
    },
  },
  created() {
    this.fetchTickets()
    this.initWebsocketListeners()
  },
  beforeDestroy() {
    this.websocketListeners.forEach(id => {
      this.$store.dispatch('websocket/detachFromListener', [id])
    })
  },
  destroyed() {
    clearEffect()
  },
  methods: {
    applyFilters() {
      this.tableFilters = { ...this.tableFilters }
    },
    async initWebsocketListeners() {
      const dbStoreEventId = await this.$store.dispatch('websocket/attachToListener', [
        'DBStoreEvent',
        (e) => {
          if (e?.model !== 'App\\Models\\ConsumerTicketRequest') return
          this.fetchTickets()
        },
      ])
      const dbUpdateEventId = await this.$store.dispatch('websocket/attachToListener', [
        'DBUpdateEvent', (e) => {
          if (e?.model !== 'App\\Models\\ConsumerTicketRequest') return
          this.fetchTickets()
        },
      ])
      this.websocketListeners.push(dbStoreEventId, dbUpdateEventId)
    },
    fetchTickets() {
      watchEffect(async (onCleanup) => {
        try {
          this.table = { ...this.table, loading: true }
          const controller = new AbortController()
          onCleanup(() => controller.abort())

          const response = await authAxios().get('/v2/consumer-ticket-requests', {
            params: { status: this.tableFilters.status, search: this.tableFilters.search },
            signal: controller.signal,
          })

          this.table = {
            ...this.table,
            items: response.data.data,
          }
        } catch (e) {
          console.error(e)
          LoadingPopup.close()
          ErrorPopup.fire('Error', 'Oops something went wrong')
        } finally {
          this.table = { ...this.table, loading: false }
        }
      }, 'ConsumerTicketRequestsIndex_fetchTickets')
    },
    dateFormatWithTime,
    async reviewTicket(item) {
      try {
        LoadingPopup.fire('Reviewing', 'reviewing consumer ticket')
        const { data } = await authAxios().put(`/v2/consumer-ticket-requests/${item.id}/review`)
        SuccessPopup.fire('Success', data?.message || 'consumer ticket set to reviewing')
        this.fetchTickets()
      } catch (e) {
        ErrorPopup.fire('Error', 'Failed to mark as reviewing')
      }
    },
    async closeTicket(item) {
      try {
        const { isConfirmed, value: reply } = await Swal.fire({
          title: 'Enter your reply',
          input: 'text',
          inputLabel: 'Reply',
          showCancelButton: true,
          inputValidator(value) {
            if (!value) return 'reply is required'
          },
        })
        if (!isConfirmed) return
        LoadingPopup.fire('Closing', 'closing consumer ticket!!!')
        const { data } = await authAxios().put(`/v2/consumer-ticket-requests/${item.id}/close`, { reply })
        SuccessPopup.fire('Success', data?.message || 'consumer ticket successfully closed')
        this.fetchTickets()
      } catch (e) {
        ErrorPopup.fire('Error', 'Failed to close ticket')
      }
    },
  },
}
</script>
